import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Rocket, Bot, Globe2, Zap, Fingerprint, Package, Layers, Boxes, Database, ShieldCheck, Clapperboard, Mic, MessagesSquare, Shirt, Film, Lock, Orbit, X, ArrowRight, Check } from "lucide-react";
import { BootSequence } from "@/components/BootSequence";
import { MatrixRain } from "@/components/MatrixRain";
import { Particles } from "@/components/Particles";
import { SystemStatus } from "@/components/SystemStatus";
import { Logo } from "@/components/Logo";
import { HoloCard, NeonButton, accentColor } from "@/components/Holo";
import { useAuth } from "@/context/AuthContext";
import { enterApp, docsHref } from "@/lib/site";
import api from "@/lib/api";

const FEATURES = [
  { icon: Fingerprint, name: "Identity Matrix™", accent: "pink", desc: "Character consistency engine.", locks: ["Faces", "Skin tones", "Body shapes", "Hair", "Clothing", "Expressions", "Personalities"] },
  { icon: Package, name: "Brand Core™", accent: "turquoise", desc: "Product consistency system.", locks: ["Logos", "Packaging", "Bottle sizes", "Labels", "Colors", "Assets"] },
  { icon: Layers, name: "Scene Memory™", accent: "green", desc: "AI remembers your worlds.", locks: ["Backgrounds", "Lighting", "Locations", "Camera angles", "Continuity"] },
  { icon: Clapperboard, name: "Neural Director™", accent: "yellow", desc: "AI remembers your direction across scenes.", locks: ["Camera movement", "Expressions", "Dialogue style", "Clothing continuity", "Story progression"] },
  { icon: Boxes, name: "World Forge™", accent: "purple", desc: "Campaign builder.", locks: ["Ads", "Commercials", "Fashion", "Movie scenes", "TikTok", "Social"] },
  { icon: Database, name: "Memory Vault™", accent: "yellow", desc: "Save everything, forever.", locks: ["Characters", "Products", "Worlds", "Campaigns", "Scenes", "Templates"] },
  { icon: ShieldCheck, name: "Credit Shield™", accent: "turquoise", desc: "Failed generations refund credits.", locks: ["Refund system", "Failure detection", "Quality monitoring", "Warnings"] },
];

const USERS = ["Fashion Designers", "Beauty Brands", "Movie Studios", "TikTok Shops", "Marketing Agencies", "Content Creators", "E-commerce", "AI Influencers"];

const COMING = [
  { icon: Clapperboard, name: "Neural Director™", accent: "yellow", status: "ONLINE" },
  { icon: Mic, name: "Voice Memory™", accent: "turquoise", status: "SOON" },
  { icon: MessagesSquare, name: "Dialogue Engine™", accent: "pink", status: "SOON" },
  { icon: Shirt, name: "Fashion Core™", accent: "purple", status: "SOON" },
  { icon: Film, name: "Cinema Builder™", accent: "green", status: "SOON" },
  { icon: Lock, name: "Product Lock™", accent: "turquoise", status: "SOON" },
  { icon: Orbit, name: "Universe Memory™", accent: "pink", status: "SOON" },
];

const Nav = ({ onEnter }) => {
  const { user } = useAuth();
  return (
    <nav className="fixed top-0 inset-x-0 z-50 glass border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
        <Logo variant="gold" className="h-14" />
        <div className="hidden md:flex items-center gap-8 font-mono-tech text-[11px] uppercase tracking-widest text-white/60">
          <a href="#features" className="hover:text-neon-turquoise transition-colors" data-testid="nav-features">Features</a>
          <a href="#pricing" className="hover:text-neon-turquoise transition-colors" data-testid="nav-pricing">Pricing</a>
          <a href="#users" className="hover:text-neon-turquoise transition-colors" data-testid="nav-users">Creators</a>
          <a href={docsHref()} className="hover:text-neon-turquoise transition-colors" data-testid="nav-docs">Docs</a>
        </div>
        <NeonButton accent="turquoise" onClick={onEnter} data-testid="nav-enter-btn">
          {user ? "Command Center" : "Enter FUTRA"} <ArrowRight size={14} />
        </NeonButton>
      </div>
    </nav>
  );
};

export default function Landing() {
  const [booted, setBooted] = useState(() =>
    sessionStorage.getItem("futra_booted") === "1" ||
    new URLSearchParams(window.location.search).get("skipboot") === "1"
  );
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const finishBoot = () => { sessionStorage.setItem("futra_booted", "1"); setBooted(true); };
  const enter = () => enterApp(navigate, user ? "/app" : "/login");
  const register = () => enterApp(navigate, user ? "/app" : "/register");

  if (!booted) return <BootSequence onDone={finishBoot} />;

  return (
    <div className="relative min-h-screen bg-[#050505] grain text-white overflow-hidden">
      <div className="crt-overlay" />
      <Nav onEnter={enter} />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-16">
        <div className="absolute inset-0"><MatrixRain opacity={0.16} /></div>
        <div className="absolute inset-0"><Particles opacity={0.5} /></div>
        <div className="absolute inset-0 cyber-grid opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
        <div className="scanline" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 w-full grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 border border-white/15 px-3 py-1 mb-8 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-neon-turquoise">
              <span className="w-1.5 h-1.5 bg-neon-green rounded-full animate-pulse-glow" /> Neural Core Online
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              AI THAT <span className="text-glow-turq">FINALLY</span><br />REMEMBERS.
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
              className="mt-6 text-white/60 text-base sm:text-lg max-w-lg font-mono-tech">
              Create Once. Stay Consistent Forever. FUTRA is the operating system for AI creators — locking faces, products, scenes & campaigns across every generation.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              className="mt-10 flex flex-wrap gap-4">
              <NeonButton accent="turquoise" onClick={enter} data-testid="hero-enter-btn"><Rocket size={15} /> Enter FUTRA</NeonButton>
              <NeonButton accent="pink" onClick={register} data-testid="hero-upload-btn"><Bot size={15} /> Initialize Identity</NeonButton>
              <NeonButton accent="purple" onClick={register} data-testid="hero-campaign-btn"><Globe2 size={15} /> Forge Universe</NeonButton>
            </motion.div>
            <SystemStatus />
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
            className="relative hidden lg:block">
            <div className="animate-float">
              <HoloCard accent="pink" className="p-1.5" hover={false}>
                <img src="https://images.unsplash.com/photo-1779832485996-bd8f1252d42b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                  alt="Consistency locked subject" className="w-full h-[440px] object-cover clip-corner" />
              </HoloCard>
            </div>
            <div className="absolute -bottom-5 -left-5 glass clip-corner border border-white/10 px-5 py-3 font-mono-tech text-[11px]"
              style={{ borderTop: "1px solid rgba(0,240,255,0.6)" }}>
              <span className="text-neon-turquoise">IDENTITY LOCKED</span> · 99.4% match
            </div>
          </motion.div>
        </div>
      </section>

      {/* MISSION STRIP */}
      <section className="relative border-y border-white/10 bg-[#0a0a0c]">
        <div className="max-w-7xl mx-auto px-5 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 font-mono-tech text-center">
          {[["FACES", "Locked"], ["PRODUCTS", "Sized"], ["SCENES", "Consistent"], ["CREDITS", "Shielded"]].map(([a, b]) => (
            <div key={a} data-testid={`strip-${a}`}>
              <div className="text-neon-turquoise text-xl font-bold">{b}</div>
              <div className="text-white/40 text-[10px] uppercase tracking-[0.25em] mt-1">{a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 scan-lines opacity-40" />
        <div className="neon-sweep" />
        <div className="relative z-10 max-w-4xl mx-auto px-5">
          <p className="font-mono-tech text-neon-pink text-[11px] uppercase tracking-[0.3em] mb-4">// The Problem</p>
          <h2 className="font-display font-black text-3xl sm:text-5xl leading-tight">OTHER AI TOOLS <span className="text-glow-pink">FORGET.</span></h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-4 font-mono-tech">
            {["Faces change.", "Products resize.", "Stories break.", "Credits get wasted."].map((t, i) => (
              <div key={t} data-testid={`problem-${i}`} className="flex items-center gap-3 border border-white/10 px-4 py-3 text-white/45">
                <X size={16} className="text-neon-pink shrink-0" /> <span className="line-through decoration-neon-pink/60">{t}</span>
              </div>
            ))}
          </div>
          <h3 className="mt-12 font-display font-bold text-2xl sm:text-4xl">FUTRA was built to <span className="text-glow-turq">remember.</span></h3>
          <p className="mt-4 text-white/50 font-mono-tech max-w-xl">Every face, product, world and story — locked into Memory™ and recalled perfectly across every generation.</p>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="relative py-28">
        <div className="max-w-7xl mx-auto px-5">
          <div className="mb-14">
            <p className="font-mono-tech text-neon-pink text-[11px] uppercase tracking-[0.3em] mb-3">// Core Systems</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl">The Consistency Engine</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => {
              const c = accentColor(f.accent);
              return (
                <motion.div key={f.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
                  <HoloCard accent={f.accent} testid={`feature-${i}`} className="p-6 h-full">
                    <div className="w-11 h-11 flex items-center justify-center border border-white/15 mb-5"
                      style={{ color: c.hex, boxShadow: `0 0 14px rgba(${c.rgb},0.25)` }}>
                      <f.icon size={20} strokeWidth={1.5} />
                    </div>
                    <h3 className="font-display font-bold text-lg">{f.name}</h3>
                    <p className="text-white/50 text-sm mt-1 font-mono-tech">{f.desc}</p>
                    <div className="flex flex-wrap gap-2 mt-5">
                      {f.locks.map((l) => (
                        <span key={l} className="font-mono-tech text-[10px] uppercase tracking-wider border border-white/10 px-2 py-1 text-white/50">{l}</span>
                      ))}
                    </div>
                  </HoloCard>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMING ONLINE */}
      <section className="relative py-28 border-y border-white/10 bg-[#08080a] overflow-hidden">
        <div className="absolute inset-0"><Particles count={30} opacity={0.4} /></div>
        <div className="relative z-10 max-w-7xl mx-auto px-5">
          <p className="font-mono-tech text-neon-green text-[11px] uppercase tracking-[0.3em] mb-3">// Roadmap</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl">COMING ONLINE<span className="animate-blink text-neon-turquoise">_</span></h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {COMING.map((m, i) => {
              const c = accentColor(m.accent);
              const online = m.status === "ONLINE";
              return (
                <motion.div key={m.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                  <HoloCard accent={m.accent} testid={`coming-${i}`} className="p-5 flex items-center justify-between animate-border-glow">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center border border-white/15" style={{ color: c.hex }}>
                        <m.icon size={18} strokeWidth={1.5} />
                      </div>
                      <span className="font-display font-bold text-sm">{m.name}</span>
                    </div>
                    <span className="font-mono-tech text-[9px] uppercase tracking-widest px-2 py-1 border"
                      style={online
                        ? { color: "#39ff14", borderColor: "rgba(57,255,20,0.4)" }
                        : { color: c.hex, borderColor: `rgba(${c.rgb},0.35)` }}>
                      <span className={online ? "" : "animate-pulse-glow"}>{online ? "● Online" : "◌ Initializing"}</span>
                    </span>
                  </HoloCard>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="relative py-28 bg-[#08080a] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5">
          <div className="mb-14">
            <p className="font-mono-tech text-neon-turquoise text-[11px] uppercase tracking-[0.3em] mb-3">// Access Tiers</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl">Choose Your Operating License</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl">
            {plans.map((p, i) => {
              const c = accentColor(p.accent);
              return (
                <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
                  <HoloCard accent={p.accent} testid={`plan-${p.name}`} className={`p-6 h-full flex flex-col ${p.highlight ? "scale-[1.03]" : ""}`}>
                    {p.highlight && <span className="absolute top-4 right-4 font-mono-tech text-[9px] uppercase tracking-widest px-2 py-0.5" style={{ color: c.hex, border: `1px solid ${c.hex}` }}>Popular</span>}
                    <h3 className="font-display font-bold text-xl" style={{ color: c.hex }}>{p.name}</h3>
                    <div className="mt-4 flex items-end gap-1">
                      {p.price_label ? (
                        <span className="font-display font-black text-4xl" style={{ color: c.hex }}>{p.price_label}</span>
                      ) : (
                        <>
                          <span className="font-display font-black text-4xl">${p.price}</span>
                          <span className="text-white/40 font-mono-tech text-xs mb-1">/mo</span>
                        </>
                      )}
                    </div>
                    <p className="font-mono-tech text-[11px] text-white/50 mt-2">{p.price_label ? "Tailored to your studio" : p.credits.toLocaleString() + " credits"}</p>
                    <div className="h-px bg-white/10 my-5" />
                    <ul className="space-y-3 flex-1">
                      {p.features.map((ft) => (
                        <li key={ft} className="flex gap-2 text-sm text-white/70">
                          <Check size={15} style={{ color: c.hex }} className="shrink-0 mt-0.5" /> {ft}
                        </li>
                      ))}
                    </ul>
                    <NeonButton accent={p.accent} className="mt-6 w-full" onClick={() => enterApp(navigate, user ? "/app" : "/register")} data-testid={`plan-cta-${p.name}`}>
                      Select {p.price_label ? "Enterprise" : p.name}
                    </NeonButton>
                  </HoloCard>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* USERS */}
      <section id="users" className="relative py-24">
        <div className="max-w-7xl mx-auto px-5">
          <p className="font-mono-tech text-neon-purple text-[11px] uppercase tracking-[0.3em] mb-3">// Built For</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl mb-10">Every Creator In The Grid</h2>
          <div className="flex flex-wrap gap-3">
            {USERS.map((u) => (
              <span key={u} className="font-mono-tech text-sm border border-white/10 px-4 py-2 text-white/70 hover:border-neon-turquoise hover:text-neon-turquoise transition-colors" data-testid={`user-type-${u}`}>{u}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 text-center border-t border-white/10 bg-[#08080a]">
        <div className="absolute inset-0"><MatrixRain opacity={0.1} color="#ff007f" /></div>
        <div className="relative z-10 max-w-3xl mx-auto px-5">
          <Logo variant="gold" className="h-24 mx-auto mb-8" />
          <h2 className="font-display font-black text-3xl sm:text-5xl leading-tight">Reimagine Everything.</h2>
          <p className="text-white/50 mt-4 font-mono-tech">The future of creation begins when your AI stops forgetting.</p>
          <div className="mt-10 flex justify-center">
            <NeonButton accent="pink" onClick={enter} data-testid="cta-enter-btn"><Rocket size={15} /> Enter FUTRA</NeonButton>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10 text-center">
        <Logo variant="gold" className="h-10 mx-auto mb-4" />
        <p className="font-mono-tech text-[11px] text-neon-turquoise tracking-widest">AI That Finally Remembers™</p>
        <p className="font-mono-tech text-[10px] text-white/40 mt-1">Create Once. Stay Consistent Forever.</p>
        <p className="font-mono-tech text-[10px] text-white/30 mt-4">FUTRA OS™ · <a href="https://futra.pro" className="text-white/50 hover:text-neon-turquoise transition-colors">futra.pro</a> · © 2077 FUTRA</p>
      </footer>
    </div>
  );
}
